import { BarChart as BarChartIcon, MultilineChart as MultilineChartIcon, TableChart } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";

import { useLensTimelineData } from "../../../data/hooks/usePerformanceAnalysis";
import { CONVERSATION_LENS } from "../../../utils/terminology";
import { TimeRange } from "../../../utils/timerange";
import LensTimelineBarChart from "./LensTimelineBarChart";
import LensTimelineLineChart from "./LensTimelineLineChart";
import LensTimelineTable from "./LensTimelineTable";

interface LensTimelineViewProps {
  lensName: string;
  timeRange: TimeRange;
  timezone: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
  groupAvgScore?: number;
  groupBestAvgScore?: number;
}

type ViewMode = "barchart" | "table" | "linechart";

const LensTimelineView = ({
  lensName,
  timeRange,
  timezone,
  userFilter = "all",
  permittedEmails = null,
  groupAvgScore = 0,
  groupBestAvgScore = 0
}: LensTimelineViewProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("linechart");

  const {
    data: timelineData,
    isLoading,
    error
  } = useLensTimelineData({
    timeRange,
    timezone,
    lensName,
    userFilter,
    permittedEmails,
    enabled: !!lensName && lensName !== "all"
  });

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Select the appropriate data based on user filter
  const displayData = useMemo(() => {
    if (!timelineData) {
      return [];
    }

    // If filtering by specific user, show individual user data
    // Otherwise show aggregated data for all users
    return userFilter === "all" ? timelineData.all : timelineData.byUser;
  }, [timelineData, userFilter]);

  // Prepare data for DataGrid
  const tableRows = useMemo(() => {
    if (!displayData || displayData.length === 0) {
      return [];
    }

    return displayData.map((row, index) => ({
      id: `${row.user_email}-${row.date}-${index}`,
      user_email: row.user_email,
      date: row.date,
      dateFormatted: format(parseISO(row.date), "MMM d, yyyy"),
      min_score: row.min_score,
      max_score: row.max_score,
      avg_score: row.avg_score,
      stddev_score: row.stddev_score,
      median_score: row.median_score,
      q1_score: row.q1_score,
      q3_score: row.q3_score,
      scored_count: row.scored_count,
      not_scored_count: row.not_scored_count,
      avg_score_5d_ma: row.avg_score_5d_ma
    }));
  }, [displayData]);

  // Prepare data for charts
  const barChartData = useMemo(() => {
    if (!displayData || displayData.length === 0) {
      return {
        labels: [],
        avgScores: [],
        minScores: [],
        maxScores: [],
        countScores: [],
        avgScores5dMA: []
      };
    }

    return {
      labels: displayData.map(data => format(data.date, "MMM d")),
      avgScores: displayData.map(data => data.avg_score === 0 ? null : data.avg_score),
      minScores: displayData.map(data => data.min_score === 0 ? null : data.min_score),
      maxScores: displayData.map(data => data.max_score === 0 ? null : data.max_score),
      countScores: displayData.map(data => data.scored_count),
      avgScores5dMA: displayData.map(data => data.avg_score_5d_ma)
    };
  }, [displayData]);

  const columns: GridColDef[] = useMemo(() => {
    // User column is not shown because:
    // - When userFilter === "all": all rows show user_email = 'ALL' (redundant)
    // - When userFilter is specific: all rows show the same user email (redundant)
    const baseColumns: GridColDef[] = [
      {
        field: "dateFormatted",
        headerName: "Date",
        width: 130,
        sortable: true
      },
      {
        field: "avg_score",
        headerName: "Avg Score",
        flex: 1,
        minWidth: 110,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "avg_score_5d_ma",
        headerName: "5D MA",
        width: 100,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "min_score",
        headerName: "Min Score",
        width: 110,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "max_score",
        headerName: "Max Score",
        width: 110,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "median_score",
        headerName: "Median",
        width: 100,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "q1_score",
        headerName: "Q1",
        width: 90,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "q3_score",
        headerName: "Q3",
        width: 90,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "stddev_score",
        headerName: "Std Dev",
        width: 100,
        type: "number",
        valueFormatter: (value: number | null) => value?.toFixed(2) || "-"
      },
      {
        field: "scored_count",
        headerName: "# Scored",
        width: 110,
        type: "number",
        align: "right"
      },
      {
        field: "not_scored_count",
        headerName: "# Not Scored",
        width: 130,
        type: "number",
        align: "right"
      }
    ];

    return baseColumns;
  }, []);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Alert severity="error">
          Failed to load lens timeline data: {error.message}
        </Alert>
      </Paper>
    );
  }

  if (!displayData || displayData.length === 0) {
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body2" align="center" sx={{ py: 2 }}>
          No timeline data available for this lens
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 3 }}>
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6">{CONVERSATION_LENS} Performance Timeline</Typography>
          <Typography variant="body2" color="text.secondary">
            {lensName} - {userFilter === "all"
              ? "Aggregated daily performance across all users"
              : `Daily performance for ${userFilter}`}
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="linechart" aria-label="line chart view">
            <MultilineChartIcon sx={{ mr: 0.5 }} fontSize="small" />
            Line Chart
          </ToggleButton>
          <ToggleButton value="barchart" aria-label="bar chart view">
            <BarChartIcon sx={{ mr: 0.5 }} fontSize="small" />
            Bar Chart
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChart sx={{ mr: 0.5 }} fontSize="small" />
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === "linechart" ? (
        <Box sx={{ p: 2, width: "100%", height: 400 }}>
          <LensTimelineLineChart
            labels={barChartData.labels}
            avgScores={barChartData.avgScores}
            minScores={barChartData.minScores}
            maxScores={barChartData.maxScores}
            countScores={barChartData.countScores}
            avgScores5dMA={barChartData.avgScores5dMA}
            teamBest={groupBestAvgScore}
            teamAvg={groupAvgScore}
          />
        </Box>
      ) : viewMode === "barchart" ? (
        <Box sx={{ p: 2, width: "100%", height: 400 }}>
          <LensTimelineBarChart
            labels={barChartData.labels}
            avgScores={barChartData.avgScores}
          />
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <LensTimelineTable rows={tableRows} columns={columns} />
        </Box>
      )}
    </Paper>
  );
};

export default LensTimelineView;
